import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { dentalApi, dentalQueryKeys, type ToothState } from "../../../shared/api/dental";
import { ErrorState } from "../../../shared/ui/ErrorState";
import { DentalTreatmentPlans } from "./dental/DentalTreatmentPlans";
import {
  CONDITION_DOT,
  CONDITION_LABEL,
  CONDITIONS,
  FDI_LOWER,
  FDI_UPPER,
  SURFACES,
} from "./dental/odontogram";

/**
 * Interactive FDI odontogram (#18636). Click a tooth → record its condition (+ optional surface/note);
 * teeth are coloured by their current state. Below: recent findings + multi-session treatment plans.
 * The parent (PatientDetailPage) only mounts this tab when `GET /dental/capability` says the clinic is
 * a dental vertical, so this component assumes it's enabled.
 */
/**
 * @param encounterId the lượt khám this chart is being edited from, when there is one (#18868).
 *   Optional: the odontogram is longitudinal data about a *patient*, not about one visit — the
 *   encounter is the clinical *context* a change was made in, not its owner. Opened from the
 *   patient record there is no such context, and findings are recorded without one exactly as
 *   before.
 */
export function DentalChartPanel({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId?: string | null;
}) {
  const qc = useQueryClient();
  const chart = useQuery({
    queryKey: dentalQueryKeys.chart(patientId),
    queryFn: () => dentalApi.getChart(patientId),
  });

  const [selectedTooth, setSelectedTooth] = useState<string | null>(null);
  const [condition, setCondition] = useState<string>("CARIES");
  const [surface, setSurface] = useState<string>("whole");
  const [note, setNote] = useState("");

  const record = useMutation({
    mutationFn: () =>
      dentalApi.recordFinding(patientId, {
        toothFdi: selectedTooth!,
        condition,
        surface: surface === "whole" ? null : surface,
        note: note.trim() || null,
        encounterId: encounterId ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dentalQueryKeys.chart(patientId) });
      closeDialog();
    },
  });

  function openTooth(fdi: string, current?: ToothState) {
    setSelectedTooth(fdi);
    setCondition(current?.condition ?? "CARIES");
    setSurface(current?.surfaces || "whole");
    setNote(current?.note ?? "");
  }
  function closeDialog() {
    setSelectedTooth(null);
    setNote("");
    setSurface("whole");
    setCondition("CARIES");
  }

  if (chart.isLoading) return <p className="text-sm text-muted-foreground">Đang tải sơ đồ răng…</p>;
  if (chart.isError || !chart.data) return <ErrorState error={chart.error} />;

  const byTooth = new Map(chart.data.teeth.map((t) => [t.toothFdi, t]));

  const renderRow = (row: readonly string[]) => (
    <div className="flex flex-wrap justify-center gap-1">
      {row.map((fdi, idx) => {
        const st = byTooth.get(fdi);
        const cond = st?.condition ?? "SOUND";
        return (
          <button
            key={fdi}
            type="button"
            title={`R${fdi} — ${CONDITION_LABEL[cond] ?? cond}`}
            onClick={() => openTooth(fdi, st)}
            className={cn(
              "flex h-11 w-9 flex-col items-center justify-center rounded border text-[11px] font-medium transition-transform hover:scale-105",
              CONDITION_DOT[cond] ?? CONDITION_DOT.SOUND,
              // Visual gap between the two quadrants (after the 8th tooth).
              idx === 8 && "ml-3",
            )}
          >
            <span className="text-base leading-none">🦷</span>
            <span className="mt-0.5 leading-none">{fdi}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">Sơ đồ răng (FDI)</h3>
            <span className="text-xs text-muted-foreground">Chọn răng để ghi tình trạng</span>
          </div>

          <div className="space-y-2 overflow-x-auto py-2">
            {renderRow(FDI_UPPER)}
            <div className="mx-auto h-px w-full max-w-md bg-border" />
            {renderRow(FDI_LOWER)}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 pt-2">
            {CONDITIONS.map((c) => (
              <span key={c.value} className="flex items-center gap-1.5 text-xs">
                <span className={cn("inline-block h-3 w-3 rounded-sm border", c.dot)} />
                {c.label}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent findings */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <h3 className="text-base font-semibold">Ghi nhận gần đây</h3>
          {chart.data.recentFindings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có ghi nhận nào.</p>
          ) : (
            <ul className="divide-y divide-border">
              {chart.data.recentFindings.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="font-medium">R{f.toothFdi}</span>
                  {f.surface && <span className="text-xs text-muted-foreground">({f.surface})</span>}
                  <span
                    className={cn(
                      "rounded-sm border px-1.5 py-0.5 text-xs",
                      CONDITION_DOT[f.condition] ?? CONDITION_DOT.SOUND,
                    )}
                  >
                    {CONDITION_LABEL[f.condition] ?? f.condition}
                  </span>
                  {f.note && <span className="text-muted-foreground">— {f.note}</span>}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(f.recordedAt).toLocaleString("vi-VN")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Treatment plans */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="text-base font-semibold">Kế hoạch điều trị</h3>
          <DentalTreatmentPlans patientId={patientId} encounterId={encounterId} />
        </CardContent>
      </Card>

      {/* Record-finding dialog */}
      <Dialog open={!!selectedTooth} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ghi tình trạng răng {selectedTooth}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Tình trạng</label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Mặt răng</label>
              <Select value={surface} onValueChange={setSurface}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SURFACES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Ghi chú</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Tuỳ chọn"
              />
            </div>
            {record.isError && <ErrorState error={record.error} inline />}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={closeDialog}>
              Huỷ
            </Button>
            <Button type="button" disabled={record.isPending} onClick={() => record.mutate()}>
              {record.isPending ? "Đang lưu…" : "Lưu ghi nhận"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
